import { useCallback, useEffect, useState } from 'react';
import { logoSources } from '@pons/contracts/logoSources';
import { isUsdReady } from './market';

import type { Round } from '@pons/contracts';
export type { Choice, Round } from '@pons/contracts';
export const isOpen = (r: Round, now: number) =>
  !r.outcome &&
  r.canonical !== false &&
  isUsdReady(r, now) &&
  (r.ruleVersion !== 'usd-v2' || Number(r.p0Usd) > 0) &&
  now >= r.start &&
  now < r.predictionClosesAt;
export const isWatching = (r: Round, now: number) =>
  !r.outcome && now >= r.predictionClosesAt && now < r.settlesAt;
export function duration(ms: number) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)
    .toString()
    .padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
}
export function ago(time: number, now: number) {
  const s = Math.max(0, Math.floor((now - time) / 1000));
  return s < 60
    ? s + 's ago'
    : s < 3600
      ? Math.floor(s / 60) + 'm ago'
      : s < 86400
        ? Math.floor(s / 3600) + 'h ago'
        : Math.floor(s / 86400) + 'd ago';
}
export function change(r: Round) {
  return r.currentPriceUsd != null && r.p0Usd != null && Number(r.p0Usd) > 0
    ? (Number(r.currentPriceUsd) / Number(r.p0Usd) - 1) * 100
    : null;
}
export function usd(value: string | null | undefined) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return '$' + number(Number(value));
}
export function number(n: number | null) {
  if (n === null || !Number.isFinite(n)) return '—';
  if (n > 0 && n < 0.0001) return '<0.0001';
  return n.toLocaleString(
    'en-US',
    n >= 10000
      ? { notation: 'compact', maximumFractionDigits: 2 }
      : { maximumFractionDigits: n >= 1 ? 2 : 4 },
  );
}
export function Art({
  symbol,
  r,
  className = '',
}: {
  symbol: string;
  r?: Round;
  className?: string;
}) {
  return (
    <TokenLogo
      key={(r?.address || '') + ':' + (r?.logo || 'missing')}
      address={r?.address}
      source={r?.logo || null}
      symbol={symbol}
      className={className}
    />
  );
}
function TokenLogo({
  address,
  source,
  symbol,
  className,
}: {
  address?: string;
  source: string | null;
  symbol: string;
  className: string;
}) {
  let version = 0;
  for (const char of source || '')
    version = (Math.imul(version, 31) + char.charCodeAt(0)) >>> 0;
  const primary =
    address && source
      ? '/api/token-logo/' + address + '?v=' + version.toString(36)
      : null;
  const ipfs = logoSources(source).length > 1;
  const candidates = [
    ...(primary ? [primary] : []),
    ...(!ipfs && source ? [source] : []),
  ];
  const [attempt, setAttempt] = useState(0),
    [retry, setRetry] = useState(0),
    [failed, setFailed] = useState(false),
    [loaded, setLoaded] = useState(false);
  const candidate = candidates[attempt];
  const src = candidate?.startsWith('/api/')
    ? candidate + '&attempt=' + retry
    : candidate;
  const onError = useCallback(() => {
    setLoaded(false);
    if (attempt + 1 < candidates.length) setAttempt(attempt + 1);
    else setFailed(true);
  }, [attempt, candidates.length]);
  useEffect(() => {
    if (!failed || retry >= 3 || !source) return;
    const timer = setTimeout(() => {
      setAttempt(0);
      setRetry((v) => v + 1);
      setLoaded(false);
      setFailed(false);
    }, [6000, 20000, 60000][retry]);
    return () => clearTimeout(timer);
  }, [failed, retry, source]);
  useEffect(() => {
    if (!src || failed || loaded) return;
    const timer = setTimeout(onError, 15000);
    return () => clearTimeout(timer);
  }, [src, failed, loaded, onError]);
  return (
    <div
      role="img"
      aria-label={symbol + ' token logo'}
      className={'token-art live-art ' + className}
    >
      {!loaded && (
        <span
          className="logo-placeholder"
          title={source ? 'Loading token logo' : 'No logo available'}
        >
          {symbol.slice(0, 2).toUpperCase()}
        </span>
      )}
      {src && !failed && (
        <img
          key={retry + ':' + src}
          src={src}
          alt=""
          onError={onError}
          onLoad={() => setLoaded(true)}
          referrerPolicy="no-referrer"
          loading="eager"
          decoding="async"
          style={{ opacity: loaded ? 1 : 0 }}
        />
      )}
    </div>
  );
}
